import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import { HttpMainApi } from "../../interface/main-api";
import { userState } from "../../interface/MainInterface";
import { getFormattedDateTime } from "../../utils/getFormattedDateTime";
import { useHourlyWeatherInfo } from "../weather/api/useHourlyWeatherInfo";
import { useWeeklyWeatherInfo } from "../weather/api/useWeeklyWeatherInfo";
import {
  getRobotCleanerWeatherHours,
  getRobotCleanerWeatherSummary,
  getRobotCleanerWeeklyWeatherSummary,
  getWeatherIcon,
  isRobotCleanerCautionWeather,
} from "../weather/weatherUtils";

interface propsType {
  userState: userState;
  reservationUser?: userState;
  adminMode?: boolean;
  hideNavigation?: boolean;
  reloadKey?: number;
  onReservationSuccess?: () => void;
}

const mainApi = new HttpMainApi();
const WEEK_LABELS = ["일", "월", "화", "수", "목", "금", "토"];
const RESERVATION_OPEN_DAYS = 30;
const RENTAL_DAYS = 7;
const RENTAL_PERIOD_LABEL = `${RENTAL_DAYS - 1}박 ${RENTAL_DAYS}일`;
const ROBOT_CLEANER_RESERVATION_OPEN_AT = "20260601000000";
const PRODUCT_NAME = "파워가드 창문로봇청소기 윈클봇울트라 PWB-U570";
const PRODUCT_URL =
  "https://brand.naver.com/pwguard/products/11489366885?is_retargeting=true&c=260101_p_Naver_product&pid=Naver&deep_link_value=https%3A%2F%2Fbrand.naver.com%2Fpwguard%2Fproducts%2F11489366885%3Fka_ref%3Dkakao%26ka_req_id%3DgLTB-Oz3UBRg7RZSEqj0AQ%26ka_template%3Dcommerce_info_cta2&dtm_source=KK&dtm_detail=gLTB-Oz3UBRg7RZSEqj0AQ&dtm_campaign=commerce_info_cta2&dtm_medium=OG";
const REVIEW_GUIDE_URL = `${process.env.PUBLIC_URL || ""}/reviews/window-robot-cleaner-guide.html`;

type RobotCleanerGuideDialog = "product" | "usageTips" | null;

function formatMonthLabel(month: string) {
  return `${month.slice(0, 4)}년 ${month.slice(4, 6)}월`;
}

function formatDateLabel(date: string) {
  return `${date.slice(0, 4)}년 ${date.slice(4, 6)}월 ${date.slice(6, 8)}일`;
}

function formatCompactRentalPeriod(startDate: string) {
  const endDate = addDays(startDate, RENTAL_DAYS - 1);
  return `${startDate.slice(0, 4)}.${startDate.slice(4, 6)}.${startDate.slice(6, 8)} → ${endDate.slice(0, 4)}.${endDate.slice(4, 6)}.${endDate.slice(6, 8)}`;
}

function formatConfirmRentalPeriod(startDate: string) {
  const endDate = addDays(startDate, RENTAL_DAYS - 1);
  return `반출 : ${startDate.slice(4, 6)}월 ${startDate.slice(6, 8)}일 09시\n반납 : ${endDate.slice(4, 6)}월 ${endDate.slice(6, 8)}일 17시`;
}

function addMonths(month: string, amount: number) {
  const date = new Date(Number(month.slice(0, 4)), Number(month.slice(4, 6)) - 1 + amount, 1);
  return `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function addDays(dateText: string, amount: number) {
  const date = new Date(
    Number(dateText.slice(0, 4)),
    Number(dateText.slice(4, 6)) - 1,
    Number(dateText.slice(6, 8)) + amount
  );
  return `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, "0")}${String(date.getDate()).padStart(
    2,
    "0"
  )}`;
}

function buildMonthDays(month: string) {
  const year = Number(month.slice(0, 4));
  const monthIndex = Number(month.slice(4, 6)) - 1;
  const firstDay = new Date(year, monthIndex, 1).getDay();
  const lastDate = new Date(year, monthIndex + 1, 0).getDate();
  const leadingBlanks = Array.from({ length: firstDay }, () => "");
  const days = Array.from({ length: lastDate }, (_, index) => {
    const day = String(index + 1).padStart(2, "0");
    return `${month}${day}`;
  });
  return [...leadingBlanks, ...days];
}

function isRobotCleanerReservationOpen(nowInfo: ReturnType<typeof getFormattedDateTime>) {
  return `${nowInfo.formatpk}${nowInfo.formathhmmss}` >= ROBOT_CLEANER_RESERVATION_OPEN_AT;
}

function getRobotCleanerReservationOpenNotice() {
  return `${Number(ROBOT_CLEANER_RESERVATION_OPEN_AT.slice(4, 6))}월 ${Number(
    ROBOT_CLEANER_RESERVATION_OPEN_AT.slice(6, 8)
  )}일부터 예약오픈합니다.`;
}

function getUserAddress(reservationUser: userState) {
  if (reservationUser.readAddress) {
    return reservationUser.readAddress;
  }
  if (reservationUser.userDong && reservationUser.userHo) {
    return `${reservationUser.userDong}동 ${reservationUser.userHo}호`;
  }
  return "";
}

interface RobotCleanerProductInfoDialogProps {
  open: boolean;
  onClose: () => void;
}

const RobotCleanerProductInfoDialog = ({ open, onClose }: RobotCleanerProductInfoDialogProps) => (
  <Dialog
    aria-labelledby="robot-cleaner-product-dialog-title"
    className="hp-dialog hp-robot-cleaner-dialog hp-robot-cleaner-product-dialog"
    open={open}
    onClose={onClose}
    fullWidth
    maxWidth="xs"
    PaperProps={{
      className: "hp-dialog__paper hp-robot-cleaner-dialog__paper",
    }}
  >
    <DialogTitle id="robot-cleaner-product-dialog-title" className="hp-robot-cleaner-product-dialog__header">
      <Box className="hp-robot-cleaner-product-dialog__header-inner">
        <Box aria-hidden="true" className="hp-robot-cleaner-product-dialog__orb" />
        <Typography className="hp-robot-cleaner-product-dialog__eyebrow" variant="overline">
          POWERGUARD
        </Typography>
        <Typography className="hp-robot-cleaner-product-dialog__title" variant="h6">
          제품 정보 안내
        </Typography>
        <Typography className="hp-robot-cleaner-product-dialog__description" variant="body2">
          예약 전 사용 제품을 확인해주세요.
        </Typography>
      </Box>
    </DialogTitle>
    <DialogContent className="hp-robot-cleaner-product-dialog__content">
      <Stack className="hp-robot-cleaner-product-dialog__stack" spacing={2}>
        <Box className="hp-robot-cleaner-product-dialog__product">
          <Typography className="hp-robot-cleaner-product-dialog__product-label" variant="caption">
            PRODUCT NAME
          </Typography>
          <Typography className="hp-robot-cleaner-product-dialog__product-name" variant="subtitle1">
            {PRODUCT_NAME}
          </Typography>
        </Box>
        <Box className="hp-robot-cleaner-product-dialog__notice">
          <Typography variant="body2">
            예약 전 제품 사양과 사용 방법은 상품 페이지에서 확인해주세요.
          </Typography>
        </Box>
      </Stack>
    </DialogContent>
    <DialogActions className="hp-robot-cleaner-product-dialog__actions">
      <Button className="hp-button hp-button--secondary" onClick={onClose} variant="outlined">
        닫기
      </Button>
      <Button
        className="hp-button hp-button--primary"
        variant="contained"
        href={PRODUCT_URL}
        target="_blank"
        rel="noopener noreferrer"
      >
        상품상세
      </Button>
    </DialogActions>
  </Dialog>
);

const RobotCleanerReviewDialog = ({ open, onClose }: RobotCleanerProductInfoDialogProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Dialog
      aria-labelledby="robot-cleaner-review-dialog-title"
      className="hp-dialog hp-robot-cleaner-dialog hp-robot-cleaner-review-dialog"
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      fullWidth
      maxWidth="md"
      PaperProps={{
        className: "hp-dialog__paper hp-robot-cleaner-review-dialog__paper",
      }}
    >
      <DialogTitle id="robot-cleaner-review-dialog-title" className="hp-robot-cleaner-review-dialog__header">
        <Typography component="span" className="hp-robot-cleaner-review-dialog__title">
          창문 로봇 청소기 사용 안내
        </Typography>
        <IconButton aria-label="닫기" onClick={onClose} className="hp-robot-cleaner-review-dialog__close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent className="hp-robot-cleaner-review-dialog__content">
        <iframe
          className="hp-robot-cleaner-review-dialog__frame"
          src={REVIEW_GUIDE_URL}
          title="창문 로봇 청소기 사용 안내"
        />
      </DialogContent>
    </Dialog>
  );
};

const ReservationRobotCleaner = (props: propsType) => {
  const navigate = useNavigate();
  const nowInfo = getFormattedDateTime();
  const reservationUser = props.reservationUser || props.userState;
  const isResidentReservationOpen = isRobotCleanerReservationOpen(nowInfo);
  const isReservationOpen = props.adminMode || isResidentReservationOpen;
  const isDateLimited = !props.adminMode;
  const [currentMonth, setCurrentMonth] = useState(nowInfo.formatmonthpk);
  const [reservableDates, setReservableDates] = useState<Set<string>>(new Set());
  const [selectedDate, setSelectedDate] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [openGuideDialog, setOpenGuideDialog] = useState<RobotCleanerGuideDialog>(null);
  const [isAgreementChecked, setIsAgreementChecked] = useState(false);
  const { data: hourlyWeatherInfo = [], isSuccess: isHourlyWeatherSuccess } = useHourlyWeatherInfo();
  const { data: weeklyWeatherData } = useWeeklyWeatherInfo();

  const monthDays = useMemo(() => buildMonthDays(currentMonth), [currentMonth]);
  const lastReservableDate = useMemo(() => addDays(nowInfo.formatpk, RESERVATION_OPEN_DAYS - 1), [nowInfo.formatpk]);
  const weatherSummariesByDate = useMemo(() => {
    const summaries = new Map<string, NonNullable<ReturnType<typeof getRobotCleanerWeatherSummary>>>();
    const weeklyWeatherInfo = weeklyWeatherData?.weeklyWeatherInfo || [];
    const forecastDates = Array.from(new Set(hourlyWeatherInfo.map((item) => item.date)));

    weeklyWeatherInfo
      .filter((item) => item.date >= nowInfo.formatpk)
      .forEach((item) => {
        const summary = getRobotCleanerWeeklyWeatherSummary(item);
        if (summary) {
          summaries.set(item.date, summary);
        }
      });

    forecastDates
      .filter((date) => date >= nowInfo.formatpk)
      .forEach((date) => {
        const hasRobotCleanerHours = getRobotCleanerWeatherHours(date, hourlyWeatherInfo).length > 0;
        if (!hasRobotCleanerHours) {
          return;
        }
        const summary = getRobotCleanerWeatherSummary(date, hourlyWeatherInfo);
        if (summary) {
          summaries.set(date, summary);
        }
      });

    return summaries;
  }, [hourlyWeatherInfo, nowInfo.formatpk, weeklyWeatherData]);
  const selectedWeatherSummary = selectedDate ? weatherSummariesByDate.get(selectedDate) : undefined;
  const selectedWeatherHours = useMemo(
    () => (selectedDate ? getRobotCleanerWeatherHours(selectedDate, hourlyWeatherInfo) : []),
    [hourlyWeatherInfo, selectedDate]
  );
  const getDateAvailability = useCallback(
    (date: string) => {
      const isPast = date < nowInfo.formatpk;
      const isOutOfOpenWindow = date > lastReservableDate;
      const isReserved = !reservableDates.has(date);
      const isUnavailableByDateLimit = isDateLimited && (isPast || isOutOfOpenWindow);
      const isUnavailableBeforeOpen = !props.adminMode && !isResidentReservationOpen;
      const isSelectable = !isUnavailableByDateLimit && !isReserved && !isLoading;
      const isReservable = isSelectable && !isUnavailableBeforeOpen;

      return {
        isPast,
        isOutOfOpenWindow,
        isReserved,
        isUnavailableByDateLimit,
        isUnavailableBeforeOpen,
        isSelectable,
        isReservable,
      };
    },
    [
      isDateLimited,
      isLoading,
      isResidentReservationOpen,
      lastReservableDate,
      nowInfo.formatpk,
      props.adminMode,
      reservableDates,
    ]
  );
  const selectedDateAvailability = selectedDate ? getDateAvailability(selectedDate) : undefined;

  const loadReservations = useCallback(async () => {
    setIsLoading(true);
    try {
      const days = buildMonthDays(currentMonth).filter(Boolean);
      const availabilityApi = (mainApi as any).get_robot_cleaner_availability;
      if (typeof availabilityApi !== "function") {
        // Keeps older mocked clients usable while all deployed clients move to the availability contract.
        const legacy = await mainApi.get_robot_cleaner_month_reservation({ month: currentMonth });
        const reserved = new Set((legacy.code === "200" ? legacy.response.result : []).map((item: any) => item.reservDate));
        setReservableDates(new Set(days.filter((date) => !reserved.has(date))));
        return;
      }
      const res = await mainApi.get_robot_cleaner_availability({
        fromDate: days[0],
        toDate: days[days.length - 1],
      });
      if (res.code === "200") {
        setReservableDates(new Set(res.response.reservableDates));
      } else {
        setReservableDates(new Set());
      }
    } catch (error) {
      alert(`예약 현황 조회 중 오류가 발생했습니다.\n${error}`);
      setReservableDates(new Set());
    } finally {
      setIsLoading(false);
    }
  }, [currentMonth]);

  useEffect(() => {
    setSelectedDate("");
    loadReservations();
  }, [currentMonth, loadReservations, props.reloadKey]);

  const userAddr = getUserAddress(reservationUser);

  const openReservationConfirm = () => {
    if (!isReservationOpen || !selectedDateAvailability?.isReservable) {
      return;
    }
    setIsAgreementChecked(false);
    setOpenConfirm(true);
  };

  const closeReservationConfirm = () => {
    setIsAgreementChecked(false);
    setOpenConfirm(false);
  };

  const reservationSave = async () => {
    if (!selectedDate) {
      alert("예약 날짜를 선택해주세요.");
      return;
    }

    setIsSaving(true);
    try {
      const res = await mainApi.put_robot_cleaner_reservation({
        reservDate: selectedDate,
        userId: reservationUser.userId,
        userName: reservationUser.userName,
        userAddr,
        inputDt: getFormattedDateTime().compactDateTime,
        ...(props.adminMode ? { adminMode: true } : {}),
      });

      if (res.code === "200") {
        closeReservationConfirm();
        setSelectedDate("");
        await loadReservations();
        if (props.onReservationSuccess) {
          props.onReservationSuccess();
        }
        if (props.adminMode) {
          alert("예약 완료되었습니다.");
        } else {
          navigate("/reservationrobotcleanerend");
        }
      } else if (res.code === "301") {
        alert("선택한 대여 기간에는 예약 가능한 로봇이 없습니다. 달력을 새로고침했습니다.");
        closeReservationConfirm();
        await loadReservations();
      } else if (res.code === "603") {
        alert("지난 날짜는 예약할 수 없습니다.");
        closeReservationConfirm();
        await loadReservations();
      } else if (res.code === "604") {
        alert(`예약은 오늘부터 ${RESERVATION_OPEN_DAYS}일 이내 날짜만 가능합니다.`);
        closeReservationConfirm();
        await loadReservations();
      } else if (res.code === "605") {
        alert("대여 금액 정보를 가져오지 못했습니다.\n관리사무소에 문의해주세요.");
        closeReservationConfirm();
      } else {
        alert(`알수 없는 에러가 발생했습니다.\n- CODE : ${res.code}`);
      }
    } catch (error) {
      alert(`예약 저장 중 오류가 발생했습니다.\n${error}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Stack
        className="hpoint-renewal hp-reservation-page hp-robot-cleaner-page"
        data-testid="robot-cleaner-reservation-page"
        spacing={2}
      >
        <Box className="hp-robot-cleaner-hero">
          <Typography className="hp-robot-cleaner-hero__eyebrow" component="span">
            WINDOW ROBOT CLEANER
          </Typography>
          <Typography className="hp-robot-cleaner-hero__title" component="h1">
            창문 로봇 청소기 예약
          </Typography>
          <Typography className="hp-robot-cleaner-hero__description">
            대여일 오전 9시부터 {RENTAL_DAYS}일 차 오후 5시까지 이용할 수 있습니다.
          </Typography>
          <Box className="hp-robot-cleaner-hero__chips" aria-label="대여 운영 정보">
            <span className="hp-chip">{RENTAL_PERIOD_LABEL}</span>
            <span className="hp-chip">10,000원</span>
          </Box>
        </Box>

        {!isReservationOpen ? (
          <Box className="hp-robot-cleaner-open-notice" role="status">
            {getRobotCleanerReservationOpenNotice()}
          </Box>
        ) : null}
        {props.adminMode ? (
          <Box className="hp-robot-cleaner-admin-notice" role="status">
            관리자 대리 예약 대상 : {reservationUser.userName} ({userAddr || "주소 정보 없음"})
          </Box>
        ) : null}

        <Box className="hp-robot-cleaner-summary hp-card hp-card--compact">
          <Typography className="hp-robot-cleaner-summary__label">예약 안내</Typography>
          <Typography className="hp-robot-cleaner-summary__description">
            {props.adminMode
              ? "관리자는 날짜 제한 없이 대리 예약할 수 있습니다. 로봇 2대의 실제 예약 가능 여부는 동일하게 적용됩니다."
              : `${RENTAL_PERIOD_LABEL} 대여이며, 오늘부터 ${RESERVATION_OPEN_DAYS}일 이내 날짜만 선택할 수 있습니다.`}
          </Typography>
          <Box className="hp-robot-cleaner-guide-actions">
            <Button
              className="hp-button hp-button--secondary"
              variant="outlined"
              onClick={() => setOpenGuideDialog("product")}
            >
              제품 정보 안내
            </Button>
            <Button
              className="hp-button hp-button--secondary"
              variant="outlined"
              onClick={() => setOpenGuideDialog("usageTips")}
            >
              사용 팁
            </Button>
          </Box>
        </Box>

        <Box className="hp-card hp-robot-cleaner-calendar" data-testid="robot-cleaner-calendar-card">
          <Stack className="hp-robot-cleaner-calendar__header" direction="row" alignItems="center" justifyContent="space-between">
            <IconButton className="hp-robot-cleaner-calendar__month-button" aria-label="이전 달" onClick={() => setCurrentMonth(addMonths(currentMonth, -1))}>
              <ArrowBackIosNewIcon fontSize="small" />
            </IconButton>
            <Typography className="hp-robot-cleaner-calendar__month" variant="subtitle1">
              {formatMonthLabel(currentMonth)}
            </Typography>
            <IconButton className="hp-robot-cleaner-calendar__month-button" aria-label="다음 달" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
              <ArrowForwardIosIcon fontSize="small" />
            </IconButton>
          </Stack>

          <Box className="hp-robot-cleaner-calendar__grid">
            {WEEK_LABELS.map((label) => (
              <Typography key={label} className="hp-robot-cleaner-calendar__weekday" align="center" variant="caption">
                {label}
              </Typography>
            ))}
            {monthDays.map((date, index) => {
              if (!date) {
                return <Box key={`blank-${index}`} className="hp-robot-cleaner-calendar__blank" />;
              }

              const isSelected = selectedDate === date;
              const weatherSummary = weatherSummariesByDate.get(date);
              const dateAvailability = getDateAvailability(date);
              const classNames = [
                "hp-robot-cleaner-calendar__date",
                isSelected ? "is-selected" : "",
                dateAvailability.isReserved ? "is-reserved" : "",
                weatherSummary?.isCaution ? "is-weather-caution" : "",
              ]
                .filter(Boolean)
                .join(" ");
              return (
                <Button
                  key={date}
                  className={classNames}
                  aria-label={String(Number(date.slice(6, 8)))}
                  variant={isSelected ? "contained" : "outlined"}
                  disabled={!dateAvailability.isSelectable}
                  onClick={() => {
                    if (!dateAvailability.isSelectable) {
                      return;
                    }
                    setSelectedDate(date);
                  }}
                >
                  <Stack className="hp-robot-cleaner-calendar__date-content" spacing={0.25} alignItems="center" justifyContent="center" aria-hidden="true">
                    <Typography className="hp-robot-cleaner-calendar__date-number" variant="body2">
                      {Number(date.slice(6, 8))}
                    </Typography>
                    {weatherSummary ? (
                      <Box
                        className="hp-robot-cleaner-calendar__weather-icon"
                        component="img"
                        src={weatherSummary.icon}
                        alt=""
                        data-testid={`robot-cleaner-weather-icon-${date}`}
                      />
                    ) : null}
                  </Stack>
                </Button>
              );
            })}
          </Box>
          <Typography className="hp-robot-cleaner-calendar__legend">
            회색 날짜는 예약이 마감되었으며, 주황 테두리는 날씨 확인이 필요한 날입니다.
          </Typography>
        </Box>

        {isLoading ? (
          <Stack className="hp-robot-cleaner-loading" direction="row" spacing={1} alignItems="center" justifyContent="center">
            <CircularProgress size={18} />
            <Typography variant="body2">예약 현황을 불러오는 중입니다.</Typography>
          </Stack>
        ) : null}

        <Box className="hp-card hp-card--compact hp-robot-cleaner-period" data-testid="robot-cleaner-period-card">
          <Typography className="hp-robot-cleaner-period__label">선택 대여 기간</Typography>
          <Typography className="hp-robot-cleaner-period__date" data-testid="robot-cleaner-period-value" variant="subtitle1">
            {selectedDate ? (
              <>
                <span aria-hidden="true">{formatCompactRentalPeriod(selectedDate)}</span>
                <span className="hp-sr-only">
                  <span>{formatDateLabel(selectedDate)}</span>부터 <span>{formatDateLabel(addDays(selectedDate, RENTAL_DAYS - 1))}</span>까지
                </span>
              </>
            ) : "예약 날짜를 선택해주세요."}
          </Typography>
          <Typography className="hp-robot-cleaner-period__time">
            {selectedDate ? `대여 시작 09:00 · 반납 ${RENTAL_DAYS}일 차 17:00` : "날짜를 선택하면 반납일과 날씨를 함께 안내합니다."}
          </Typography>
          {selectedDateAvailability?.isReserved ? (
            <Alert className="hp-robot-cleaner-alert" severity="warning">
              해당 날짜부터 {RENTAL_DAYS}일간 사용할 수 있는 로봇이 없습니다. 날씨만 확인할 수 있습니다.
            </Alert>
          ) : null}
        </Box>

        {selectedDate ? (
          <Box className="hp-card hp-card--compact hp-robot-cleaner-weather">
            {selectedWeatherSummary ? (
              <Box className="hp-robot-cleaner-weather__content">
                <Box className="hp-robot-cleaner-weather__summary">
                  <Box className="hp-robot-cleaner-weather__icon" component="img" src={selectedWeatherSummary.icon} alt="" />
                  <Box className="hp-robot-cleaner-weather__summary-copy">
                    <Typography className="hp-robot-cleaner-weather__title" variant="subtitle2">
                      디에트르 라포레 날씨
                    </Typography>
                    <Typography className="hp-robot-cleaner-weather__details" variant="body2">
                      {selectedWeatherSummary.statusText} · {Math.round(selectedWeatherSummary.minTemp)}°/
                      {Math.round(selectedWeatherSummary.maxTemp)}° · 강수확률 최대 {selectedWeatherSummary.maxRainProb}%
                    </Typography>
                  </Box>
                </Box>

                <Alert className="hp-robot-cleaner-alert" severity={selectedWeatherSummary.isCaution ? "warning" : "info"}>
                  {selectedWeatherSummary.cautionText}
                </Alert>

                {selectedWeatherHours.length > 0 ? (
                  <>
                    <Box
                      className="hp-robot-cleaner-weather__hours"
                      aria-label="시간대별 날씨 예보"
                      role="region"
                      tabIndex={0}
                    >
                      {selectedWeatherHours.map((item) => {
                        const isHourCaution = isRobotCleanerCautionWeather(item);
                        return (
                          <Box
                            key={`${item.date}-${item.time}`}
                            className={`hp-robot-cleaner-weather__hour${isHourCaution ? " is-caution" : ""}`}
                          >
                            <Typography className="hp-robot-cleaner-weather__hour-time" variant="caption">
                              {Number(item.time.slice(0, 2))}시
                            </Typography>
                            <Box
                              className="hp-robot-cleaner-weather__hour-icon"
                              component="img"
                              src={getWeatherIcon(item.rainType, item.skyStatus, item.time)}
                              alt=""
                            />
                            <Typography className="hp-robot-cleaner-weather__hour-temp" variant="body2">
                              {Math.round(item.temp)}°
                            </Typography>
                            <Typography className="hp-robot-cleaner-weather__hour-rain" variant="caption">
                              강수 {item.rainProb ?? 0}%
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                    <Typography className="hp-robot-cleaner-weather__scroll-hint" variant="caption">
                      좌우로 밀어 시간대별 날씨 더 보기
                    </Typography>
                  </>
                ) : (
                  <Alert className="hp-robot-cleaner-alert" severity="info">
                    시간대별 날씨는 오늘부터 3일 이내 날짜만 제공됩니다.
                  </Alert>
                )}
              </Box>
            ) : isHourlyWeatherSuccess ? (
              <Alert className="hp-robot-cleaner-alert" severity="info">
                시간대별 날씨는 오늘부터 3일 이내 날짜만 제공됩니다.
              </Alert>
            ) : (
              <Stack className="hp-robot-cleaner-loading" direction="row" spacing={1} alignItems="center">
                <CircularProgress size={16} />
                <Typography variant="body2">날씨 예보를 불러오는 중입니다.</Typography>
              </Stack>
            )}
          </Box>
        ) : null}

        <Stack className="hp-robot-cleaner-actions" spacing={1}>
          <Button
            className="hp-button hp-button--primary hp-sticky-cta"
            variant="contained"
            size="large"
            disabled={!isReservationOpen || !selectedDate || !selectedDateAvailability?.isReservable || isSaving}
            onClick={openReservationConfirm}
          >
            예약 신청
          </Button>
          {!props.hideNavigation ? (
            <Button
              className="hp-button hp-button--secondary hp-robot-cleaner-actions__history"
              variant="outlined"
              onClick={() => navigate("/reservationrobotcleanerend")}
            >
              내 예약 확인
            </Button>
          ) : null}
        </Stack>
      </Stack>

      <RobotCleanerProductInfoDialog
        open={openGuideDialog === "product"}
        onClose={() => setOpenGuideDialog(null)}
      />
      <RobotCleanerReviewDialog
        open={openGuideDialog === "usageTips"}
        onClose={() => setOpenGuideDialog(null)}
      />

      <Dialog
        className="hp-dialog hp-robot-cleaner-dialog hp-robot-cleaner-confirm-dialog"
        open={openConfirm}
        onClose={closeReservationConfirm}
        fullWidth
        PaperProps={{
          "aria-label": "창문 로봇 청소기 예약 확인",
          className: "hp-dialog__paper hp-robot-cleaner-dialog__paper",
        }}
      >
        <DialogContent className="hp-robot-cleaner-confirm-dialog__body">
          <Stack className="hp-robot-cleaner-confirm-dialog__content" spacing={2}>
            <Box className="hp-robot-cleaner-confirm-dialog__details">
              <Box className="hp-robot-cleaner-confirm-dialog__period">
                <Typography component="span">대여 기간 · {RENTAL_PERIOD_LABEL}</Typography>
                <Typography component="strong">
                  {selectedDate
                    ? formatConfirmRentalPeriod(selectedDate)
                    : ""}
                </Typography>
              </Box>
              <Box className="hp-robot-cleaner-confirm-dialog__meta">
                <Typography component="span">예약자</Typography>
                <Typography component="strong">{reservationUser.userName}</Typography>
                <Typography component="span">대여 비용</Typography>
                <Typography component="strong" className="hp-robot-cleaner-confirm-dialog__price">10,000원</Typography>
              </Box>
            </Box>

            {selectedWeatherSummary?.isCaution ? (
              <Alert className="hp-robot-cleaner-alert" severity="warning">{selectedWeatherSummary.cautionText}</Alert>
            ) : null}

            <Box className="hp-robot-cleaner-confirm-dialog__guide">
              <Typography className="hp-robot-cleaner-confirm-dialog__guide-title" variant="subtitle2">
                이용 전 꼭 확인해주세요
              </Typography>
              <Stack className="hp-robot-cleaner-confirm-dialog__guide-list" component="ol" spacing={1}>
                <Typography component="li" variant="body2">
                  대여비용은 <Box component="strong">1회({RENTAL_PERIOD_LABEL}) 10,000원</Box>이며, 관리비에 부과됩니다.
                </Typography>
                <Typography component="li" variant="body2">
                  미반납 시 대여 대기자가 사용하지 못하므로 대여 {RENTAL_DAYS}일 차 오후 5시까지 반납해야 합니다.
                </Typography>
                <Typography className="hp-robot-cleaner-confirm-dialog__guide-subtext" variant="caption">
                  미반납 시 경과 일수만큼 추가 비용이 부과됩니다.
                </Typography>
                <Typography component="li" variant="body2">사용 후 걸레는 세척한 후 반납하여야 합니다.</Typography>
              </Stack>
            </Box>

            <FormControlLabel
              className="hp-robot-cleaner-confirm-dialog__agreement"
              control={
                <Checkbox
                  checked={isAgreementChecked}
                  onChange={(event) => setIsAgreementChecked(event.target.checked)}
                />
              }
              label="위 안내사항을 확인했으며, 이용 조건에 동의합니다."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button className="hp-button hp-button--secondary" onClick={closeReservationConfirm}>취소</Button>
          <Button className="hp-button hp-button--primary" onClick={reservationSave} disabled={!isAgreementChecked || isSaving} autoFocus>
            신청
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ReservationRobotCleaner;
